﻿//==========================================================================================================
// Copyright (C) Nore Technology AB. All Rights Reserved.
//==========================================================================================================

// ----------------------------------------------------------------------------------------------------
// Imports
// ----------------------------------------------------------------------------------------------------

import * as React from "react";
import classNames from "classnames";
import usePaginatorStyles from "./PaginatorStyles";
import Button from "~base/Components/Primitives/Button";
import TitleSelect from "~base/Components/Primitives/TitleSelect";

// ----------------------------------------------------------------------------------------------------
// Component
// ----------------------------------------------------------------------------------------------------

export interface PaginatorProps {
    /**
     * Total amount of available pages.
     */
    totalPages: number;
    /**
     * Current page number (1-indexed)
     */
    currentPage: number;
    /**
     * Set the current page (1-indexed).
     */
    setCurrentPage: (page: number) => void;

    /**
     * Max pages to show.
     *
     * Default: 7
     */
    maxPagesToShow?: number;

    /**
     * Min pages to show at the start and end of the range.
     *
     * Default: 1
     */
    minEdgePagesToShow?: number;

    /**
     * Label of the available limits field.
     */
    limitTitle?: string;
    /**
     * Text code label of the available limits field.
     */
    limitTitleTextCode?: string;

    /**
     * Available limits (items per page) to choose from.
     */
    availableLimits?: number[];
    /**
     * Current amount of items to show per page.
     */
    currentLimit?: number;
    /**
     * Set current amount of items to show.
     */
    setCurrentLimit?: (limit: number) => void;

    /**
     * Location of pagination controls.
     */
    showControls?: "Above" | "Below" | "BothAboveAndBelow";

    /**
     * Show previous/next arrows.
     */
    showArrows?: boolean;

    children?: React.ReactNode;

    /** An identifier that will be added to the control as a custom attribute. This can be used by automatic UI tests. */
    testIdentifier?: string;

    /** An index to unique identifier fields of the same identifier in a list. */
    testIdentifierIndex?: number;
}

/**
 * A pagination wrapper that adds page controls around an iterable element.
 */
export const Paginator: React.FunctionComponent<PaginatorProps> = ({
    totalPages,
    currentPage,
    setCurrentPage,

    maxPagesToShow = 7,
    minEdgePagesToShow = 1,

    limitTitle,
    limitTitleTextCode,
    availableLimits = [],
    currentLimit,
    setCurrentLimit,
    showControls = "Above",
    showArrows = false,
    children,
    testIdentifier,
    testIdentifierIndex,
}) => {
    const classes = usePaginatorStyles();
    const baseTestIdentifier = testIdentifier ?? "Paginator";

    const showControlsAbove =
        showControls == "Above" || showControls == "BothAboveAndBelow";
    const showControlsBelow =
        showControls == "Below" || showControls == "BothAboveAndBelow";

    const renderPageButton = (
        page: number,
        content: string,
        active: boolean,
        ellipsis?: boolean
    ): React.ReactElement => (
        <Button
            key={page}
            testIdentifier={`${baseTestIdentifier}PageButton`}
            testIdentifierIndex={page}
            buttonClassName={classNames(
                classes.pageButton,
                ellipsis && classes.pageButtonEllipsis,
                active && classes.pageButtonActive
            )}
            onClick={() => {
                setCurrentPage(page);
            }}
        >
            {content}
        </Button>
    );

    const pages = [];

    // Starting edge pages
    for (
        let iPage = 1;
        iPage <= Math.min(minEdgePagesToShow, totalPages);
        iPage++
    ) {
        pages.push(
            renderPageButton(iPage, String(iPage), currentPage == iPage)
        );
    }

    const numMiddlePages = Math.min(
        totalPages,
        maxPagesToShow - minEdgePagesToShow * 2 - 1
    );

    const middlePageDesiredStart = Math.max(
        currentPage - Math.floor(numMiddlePages / 2),
        minEdgePagesToShow + 1
    );
    const middlePageEnd = Math.min(
        middlePageDesiredStart + numMiddlePages,
        totalPages - minEdgePagesToShow
    );
    const middlePageStart =
        middlePageEnd - middlePageDesiredStart == numMiddlePages
            ? middlePageDesiredStart
            : Math.max(middlePageEnd - numMiddlePages, minEdgePagesToShow + 1);

    for (let iPage = middlePageStart; iPage <= middlePageEnd; iPage++) {
        // Add ellipsis if there's more pages between the start & end than what we can show
        if (
            maxPagesToShow >= 5 + minEdgePagesToShow * 2 &&
            ((iPage == middlePageStart &&
                middlePageStart > minEdgePagesToShow + 1) ||
                (iPage == middlePageEnd &&
                    middlePageEnd + minEdgePagesToShow < totalPages))
        ) {
            pages.push(renderPageButton(iPage, "⋯", false, true));

            continue;
        }

        pages.push(
            renderPageButton(iPage, String(iPage), currentPage == iPage)
        );
    }

    // Add remaining edge pages
    for (
        let iPage = Math.max(
            totalPages - minEdgePagesToShow + 1,
            minEdgePagesToShow + 1
        );
        iPage <= totalPages;
        iPage++
    ) {
        pages.push(
            renderPageButton(iPage, String(iPage), currentPage == iPage)
        );
    }

    const paginatorControls = (
        <div className={classes.controlsContainer}>
            <div
                className={classNames(
                    classes.pageControls,
                    availableLimits.length == 0 &&
                        classes.pageControlsWithoutLimits
                )}
            >
                {showArrows && (
                    <Button
                        testIdentifier={`${baseTestIdentifier}PreviousPageButton`}
                        testIdentifierIndex={testIdentifierIndex}
                        buttonClassName={classes.pageButton}
                        onClick={() => {
                            setCurrentPage(
                                currentPage > 1 ? currentPage - 1 : 1
                            );
                        }}
                    >
                        {/* TODO: Create actual icons for this? */}
                        {"<"}
                    </Button>
                )}

                {pages}

                {showArrows && (
                    <Button
                        testIdentifier={`${baseTestIdentifier}NextPageButton`}
                        testIdentifierIndex={testIdentifierIndex}
                        buttonClassName={classes.pageButton}
                        onClick={() => {
                            setCurrentPage(
                                currentPage < totalPages
                                    ? currentPage + 1
                                    : totalPages
                            );
                        }}
                    >
                        {">"}
                    </Button>
                )}
            </div>
            {availableLimits.length > 0 && (
                <div className={classes.limitControls}>
                    <TitleSelect
                        testIdentifier={limitTitleTextCode}
                        required
                        padding={[]}
                        title={limitTitle}
                        titleTextCode={limitTitleTextCode}
                        stringValues={availableLimits.map((limit) =>
                            String(limit)
                        )}
                        value={String(currentLimit)}
                        onChange={(strValue) => {
                            setCurrentPage(1);
                            setCurrentLimit(parseInt(strValue, 10));
                        }}
                    />
                </div>
            )}
        </div>
    );

    return (
        <>
            {showControlsAbove && paginatorControls}
            {children}
            {showControlsBelow && paginatorControls}
        </>
    );
};
